
import React from 'react';
import { ThumbsUp, ThumbsDown, Verified, ExternalLink } from 'lucide-react';
import TechieScoreBar from './ui/TechieScoreBar';

const mockReviews = [
  {
    id: 1,
    author: "TechEnthusiast42",
    rating: 5,
    title: "Excellent product - highly recommended!",
    content: "This product exceeded my expectations. Great build quality and performance. The features work exactly as advertised and the setup was straightforward.",
    date: "2025-06-15",
    verified: true,
    helpful: 24,
    total_votes: 28
  },
  {
    id: 2,
    author: "CasualUser",
    rating: 4,
    title: "Good value for money",
    content: "Works well for everyday use. Some minor issues with the initial setup, but customer service was helpful. Overall satisfied with the purchase.",
    date: "2025-06-10",
    verified: true,
    helpful: 15,
    total_votes: 18
  },
  {
    id: 3,
    author: "PowerUser2024",
    rating: 3,
    title: "Mixed feelings about this product",
    content: "Has both pros and cons. Good performance but could be better for the price point. Build quality is decent but not exceptional.",
    date: "2025-06-05",
    verified: false,
    helpful: 8,
    total_votes: 15
  }
];


export function ProductReviews({ asin, rating, reviewCount }) {
  // Ensure partner tag is always available with fallback
  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG || 'dtoplist-20';
  const amazonReviewsUrl = `https://www.amazon.com/dp/${asin}?tag=${partnerTag}#customerReviews`;
  const techieScore = rating ? rating * 2 : 8.5;

  const scoreDistribution = [
    { range: '9-10', percentage: 45 },
    { range: '7-8', percentage: 30 },
    { range: '5-6', percentage: 15 },
    { range: '3-4', percentage: 7 },
    { range: '1-2', percentage: 3 }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Customer Reviews</h3>
        <a
          href={amazonReviewsUrl}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm transition-colors"
        >
          <ExternalLink size={14} />
          View all reviews on Amazon
        </a>
      </div>

      {/* TechieDay Score Summary */}
      <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-6">
        <div className="flex items-center gap-6">
          <div className="text-center min-w-[120px]">
            <div className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white text-gray-900 dark:text-gray-100">{techieScore.toFixed(1)}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">TechieDay Score</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {reviewCount.toLocaleString()} reviews
            </div>
          </div>
          
          <div className="flex-1">
            {scoreDistribution.map(({ range, percentage }) => (
              <div key={range} className="flex items-center gap-3 mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400 w-8">{range}</span>
                <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-700" 
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: range.startsWith('9') ? '#3B82F6' : 
                                     range.startsWith('7') ? '#22C55E' : 
                                     range.startsWith('5') ? '#F97316' : '#EF4444'
                    }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400 w-8">{percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sample Reviews */}
      <div className="space-y-6">
        <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Sample Reviews</h4>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              ⚠️ <strong>Disclaimer:</strong> These are sample reviews for demonstration purposes. 
              For actual customer reviews, please visit the Amazon product page.
            </p>
          </div>
        </div>

        {mockReviews.map((review) => (
          <div key={review.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{review.author}</span>
                  {review.verified && (
                    <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <Verified size={12} />
                      <span>Verified Purchase</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <TechieScoreBar 
                    score={review.rating * 2} 
                    compact={true} 
                    showLabel={false} 
                    summary={`Review Score: ${review.rating}/5`}
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">{review.date}</span>
                </div>
              </div>
            </div>
            
            <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{review.title}</h5>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{review.content}</p>
            
            <div className="flex items-center gap-4 text-sm">
              <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                <ThumbsUp size={14} />
                <span>Helpful ({review.helpful})</span>
              </button>
              <span className="text-gray-400 dark:text-gray-500">
                {review.helpful} of {review.total_votes} found this helpful
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <div className="text-center py-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Want to read more reviews or write your own?
        </p>
        <a
          href={amazonReviewsUrl}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ExternalLink size={16} />
          View Reviews on Amazon
        </a>
      </div>
    </div>
  );
}
